import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Label, Button, AlertBar, Spinner, Input } from 'scorer-ui-kit';
import styled from 'styled-components';
import { CenterAlignedContainer, LeftButton, FieldContainer, PopupTitle, AlertContainer, SelectField, CloseIcon, SpinnerDesign } from '../Style';
import Switch from 'react-switch';
import { BoxContainer } from '../components/BoxContainer';
import { getROISettings, updateROISettings } from '../apis';
import { StatusTranslate, DesiredStatuValues, TargetPixelValues } from '../constants';
import SelectColor from '../components/SelectColor';
import ColorSlider from '../components/ColorSlider';

const FormConatiner = styled.div`
    display: flex;
    justify-content: space-between;
    margin-top: 10px;
    gap: 0 15px;
`;

const GeneralBoxContainer = styled(BoxContainer)`
  width: 390px;
  marginTop: -35px;
  padding: 20px;
`;

const DesiredStatusBoxContainer = styled(BoxContainer)`
  width: 350px;
  marginTop: -35px;
  padding: 20px;
`;

const PopupContainer = styled.div`
    padding: 30px;
    width: 900px;
    height: auto;
`;

const TopContainer = styled(FieldContainer)`
  display: flex;
  justify-content: flex-start;
  align-items:flex-start;
`;

const TopCloudContainer = styled(FieldContainer)`
  display: flex;
  align-items:flex-start;
  margin-left:50px;
`;

const StatusContainer = styled(FieldContainer)`
  margin-bottom: 0px;
`;

const ButtonSwitchWrapper = styled.div`
  display: flex;
  padding-bottom: 10px;
`;
const ToggleLabel = styled(Label)`
    padding-top: 7px;
    padding-right: 20px;
`;

const InputField = styled(Input)`
  width: 241.4px;
`;

const Select = styled(SelectField)`
    margin-right: 0px;
    width: 100%;
`;

const LeftContainer = styled.div`
    width: 350px;
`;

const FieldWrapper = styled(FieldContainer)`
  align-items: center;
`;
const FieldLabel = styled(Label)`
  width: 160px;
  margin-bottom: 0;
  & > span {
    margin: 0;
  }
`;

const FieldLabelWithTooltip = styled(FieldLabel)`
  width: 85px;
  margin-bottom: 0;
  & > span {
    margin: 0;    
    max-width: 85px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;


interface IInput {
  note2?: string,
  pos_x?: number,
  cloud_show?: boolean,
  pixels?: number,
  analyzed_status?: string,
  reference_brightness_max?: number,
  desired_status?: string,
  reference_color?: string,
  reference_hue?: number,
  email_enabled?: boolean,
  camera_id?: number,
  name?: string,
  analyzed_color?: string,
  analyzed_hue?: number,
  enabled?: boolean,
  desired_color: string[],
  desired_hue_x: number | null,
  desired_hue_y: number | null,
  reference_last_update?: string,
  note?: string,
  reference_brightness_min?: number,
  pos_y?: number,
  setting_last_update?: string,
  analyzed_last_update?: string,
  id?: number,
  target_emails_count?: number
}

const ROISettings = (props: any) => {
  const { close, roi, cameraName, getCameraInfo, device,  getLatestSnapshot } = props;
  // initial Select menuList items
  const menuList = [
    {
      text: 'red',
      range: '(0-7 , 150-179)',
      isChecked: false,
    },
    {
      text: 'orange',
      range: '(8-29)',
      isChecked: false,
    },
    {
      text: 'green',
      range: '(30-89)',
      isChecked: false,
    },
    {
      text: 'blue',
      range: '(90-149)',
      isChecked: false,
    },
    {
      text: 'user_specified',
      range: '',
      isChecked: false,
    },
  ];
  const [inputs, setInputs] = useState<IInput>({
    desired_color: [],
    desired_hue_x: null,
    desired_hue_y: null
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [alert, setAlert] = useState<{ key: any, message: string, type: 'success' | 'error' | 'warning' | 'info' | undefined }>({ key: new Date(), message: '', type: undefined });
  const { t } = useTranslation(['DetectionSettings', 'DeviceSettings', 'Common']);

  const handleToggle = (checked: boolean) => {
    if(checked){
      setInputs({ ...inputs, 'enabled': checked, 'cloud_show': checked });
    }else{
      setInputs({ ...inputs, 'enabled': checked });
    }
  };

  const handleCloudShowToggle = (checked: boolean) => {
    setInputs({ ...inputs, 'cloud_show': checked });
  };

  const onInputChange = (event: any) => {
    if (event.target.name === 'pixels') {
      setInputs({ ...inputs, [event.target.name]: parseInt(event.target.value) });
    } else {
      setInputs({ ...inputs, [event.target.name]: event.target.value });
    }
  };

  const onInputMultiColor = useCallback((colorsData: string[]) => {
    
    if(colorsData.includes('user_specified')){
      setInputs((prevVal)=>{
        return {...prevVal, desired_color: colorsData};
      });
    } else {
      setInputs((prevVal)=>{
        return {...prevVal, desired_color: colorsData, desired_hue_x: null, desired_hue_y: null};
      });
    }
  },[]);
  
  const onHueSelect = useCallback((hueX: number|null, hueY: number|null)=>{
    if(inputs.desired_color.includes('user_specified')){
      setInputs((prevVal)=>{
        return {...prevVal, desired_hue_x: hueX, desired_hue_y: hueY};
      });
    }
  },[inputs.desired_color]);

  const onSave = () => {
    const jsonData = { ...inputs };

    if(jsonData.desired_color.length === 0) {
      setAlert({ key: new Date(), message: t('DetectionSettings:emptyColorSelectionError'), type: 'error' });
      return;
    }

    updateROISettings(cameraName, roi.id, jsonData)
      .then((res) => {
        if (res.data.status_code === '0') {
          setAlert({ key: new Date(), message: t('DeviceSettings:settingsUpdatedSuccessfully'), type: 'success' });
          setTimeout(() => { 
            getLatestSnapshot();
            getCameraInfo();
            close(); }, 2000);
        } else {
          setAlert({ key: new Date(), message: res.data.message, type: 'error' });
        }
      })
      .catch((err) => {
        if (err.response.data.message) {
          setAlert({ key: new Date(), message: err.response.data.message, type: 'error' });
        }
        console.error(err);
      });
  };

  const onCancel = () => {
    close();
  };

  const getSettings = useCallback(() => {
    getROISettings(cameraName, roi.id)
      .then((res) => {
        if (res.data.status_code === '0') {
          setInputs(res.data.data);
        } else {
          setAlert({ key: new Date(), message: res.data.message, type: 'error' });
        }
        setLoading(false);
      })
      .catch((err) => {
        if (err.response.data.message) {
          setAlert({ key: new Date(), message: err.response.data.message, type: 'error' });
        }
        console.error(err);
      });
  },[cameraName, roi.id]);

  useEffect(() => {
    getSettings();
  }, [getSettings]);

  return (
    <PopupContainer>
      <CloseIcon onClick={onCancel}>x</CloseIcon>
      <CenterAlignedContainer>
        <PopupTitle>{t('roiSettings')}</PopupTitle>
      </CenterAlignedContainer>
      {loading ?
        <Spinner size='large' styling={SpinnerDesign} /> :

        inputs && 
          <>
            <ButtonSwitchWrapper>
              <TopContainer>
                <ToggleLabel htmlFor='enabled' labelText={inputs.enabled ? t('enabled') : t('disabled')} />
                <Switch id='enabled' name='enabled' checked={inputs.enabled || false} onColor='#42b0f5' uncheckedIcon={false} checkedIcon={false} onChange={handleToggle} />
              </TopContainer>
              <TopCloudContainer>
                <ToggleLabel htmlFor='cloud_show' labelText={t('Common:showInCloud')} />
                <Switch id='cloud_show' name='cloud_show' checked={inputs.cloud_show || false} onColor='#42b0f5' uncheckedIcon={false} checkedIcon={false} onChange={handleCloudShowToggle} disabled={inputs.enabled} />
              </TopCloudContainer>
            </ButtonSwitchWrapper>
          
            <FormConatiner>
              <GeneralBoxContainer title={t('DeviceSettings:general')}>
                <LeftContainer>
                  <FieldWrapper>
                    <div title={device.roi_lable_name}>
                      <FieldLabelWithTooltip htmlFor='name' labelText={device.roi_lable_name} />
                    </div>
                    <InputField name='name' maxLength={64} placeholder={t('DeviceSettings:name')} value={inputs.name} onChange={onInputChange} />
                  </FieldWrapper>
                  <FieldWrapper>
                    <div title={device.roi_lable_note1}>
                      <FieldLabelWithTooltip htmlFor='note1' labelText={device.roi_lable_note1} />
                    </div>
                    <InputField name='note' maxLength={64} placeholder={t('DeviceSettings:note1')} value={inputs.note} onChange={onInputChange} />
                  </FieldWrapper>
                  <FieldWrapper>
                    <div title={device.roi_lable_note2}>
                      <FieldLabelWithTooltip htmlFor='note2' labelText={device.roi_lable_note2} />
                    </div>
                    <InputField name='note2' maxLength={64} placeholder={t('DeviceSettings:note2')} value={inputs.note2} onChange={onInputChange} />
                  </FieldWrapper>
                  <FieldWrapper>
                    <FieldLabel htmlFor='pixels' labelText={t('target')+' '+t('pixels')} />
                    <Select name='pixels' defaultValue={inputs.pixels} onChange={onInputChange}>
                      {
                        TargetPixelValues.map(({ showValue, value }, index) => <option key={index} value={value}>{showValue}</option>)
                      }
                    </Select>
                  </FieldWrapper>
                </LeftContainer>
              </GeneralBoxContainer>
            
              <DesiredStatusBoxContainer title={t('desiredStatus')}>
                <LeftContainer>
                  <FieldWrapper>
                    <FieldLabel htmlFor='desired_status' labelText={t('status')} />
                    <Select name='desired_status' defaultValue={inputs.desired_status} onChange={onInputChange}>
                      {
                        DesiredStatuValues.map(({ showValue, value }, index) => <option key={index} value={value}>{t('Common:status.' + StatusTranslate[showValue])}</option>)
                      }
                    </Select>
                  </FieldWrapper>
                  <StatusContainer>
                    <FieldLabel htmlFor='desired_color' labelText={t('color')} />
                    <SelectColor onChangeColor={onInputMultiColor} desiredColors={inputs.desired_color} menuList={menuList} />
                  </StatusContainer>
                  {
                    inputs.desired_color.includes('user_specified') && 
                      <StatusContainer>
                        <FieldLabel htmlFor='desired_color' labelText='' />
                        <ColorSlider 
                          hueSelect={onHueSelect}
                          hueX={(inputs.desired_hue_x !== null && inputs.desired_hue_x >=0 && inputs.desired_hue_x <=179) ?  inputs.desired_hue_x: 0}
                          hueY={(inputs.desired_hue_y !== null && inputs.desired_hue_y >=0 && inputs.desired_hue_y <=179)?  inputs.desired_hue_y: 179}
                        />
                      </StatusContainer>
                  }
                </LeftContainer>
              </DesiredStatusBoxContainer>
            </FormConatiner>
            <CenterAlignedContainer>
              <LeftButton design='secondary' onClick={onCancel}>{t('Common:cancel')}</LeftButton>
              <Button onClick={onSave}>{t('Common:save')}</Button>
            </CenterAlignedContainer>
          </>}

      {
        (alert.message !== '' &&
          <AlertContainer key={alert.key}>
            <AlertBar type={alert.type} message={alert.message} />
          </AlertContainer>
        )
      }
    </PopupContainer>

  );
};
export default ROISettings;
